import { useEffect, useState } from "react";
import api, { fmtSeconds, fmtMoney, fmtClockTime, formatApiErrorDetail } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Camera, BarChart3, Users, Activity, ArrowRight, Receipt, Phone, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function ClientOverview() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [report, setReport] = useState(null);
  const [invoice, setInvoice] = useState(null);

  const loadEmployees = () => api.get("/client/users").then((r) => setEmployees(r.data));

  useEffect(() => {
    // Default invoice window: last 30 days
    const today = new Date();
    const start = new Date(); start.setDate(today.getDate() - 29);
    const fmt = (d) => d.toISOString().slice(0, 10);
    Promise.all([
      api.get("/client/users"),
      api.get("/reports/summary", { params: { range_days: 7 } }),
      api.get("/invoices/generate", { params: { start_date: fmt(start), end_date: fmt(today) } }),
    ]).then(([u, r, inv]) => {
      setEmployees(u.data);
      setReport(r.data);
      setInvoice(inv.data);
    }).catch(() => {});
  }, []);

  const toggleCallMode = async (empId, enabled) => {
    try {
      await api.put(`/client/users/${empId}/call-mode`, { enabled });
      toast.success(enabled ? "Call Mode granted" : "Call Mode revoked");
      loadEmployees();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const totalH = report ? ((report.totals?.total || 0) / 3600).toFixed(1) : "0.0";
  const prodH = report ? ((report.totals?.productive || 0) / 3600).toFixed(1) : "0.0";

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">[ Client / Overview ]</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{user?.name}</h1>
          <p className="text-sm text-stone-500 mt-1">View-only access to your assigned workforce.</p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mb-6">
          {[
            { l: "Assigned Employees", v: employees.length, i: Users },
            { l: "Hours / 7d", v: totalH, s: "h", i: Activity },
            { l: "Productive / 7d", v: prodH, s: "h", i: Activity, color: "text-[#1f4d2e]" },
            { l: "Sessions", v: report?.totals?.sessions_count || 0, i: BarChart3 },
          ].map((k) => {
            const Icon = k.i;
            return (
              <div key={k.l} className="bg-white p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500">{k.l}</div>
                  <Icon className="h-4 w-4 text-stone-400" />
                </div>
                <div className={`font-mono-num text-4xl font-semibold tracking-tighter ${k.color || ""}`}>
                  {k.v}{k.s && <span className="text-xl text-stone-400 ml-1">{k.s}</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200 mb-6">
          <Link to="/client/screenshots" className="bg-white p-6 hover:bg-stone-50 transition-colors flex items-center justify-between group">
            <div>
              <Camera className="h-6 w-6 text-[#1f4d2e] mb-2" />
              <div className="font-display text-xl font-bold">Screenshots</div>
              <div className="text-sm text-stone-500 mt-1">Browse captures by employee and date</div>
            </div>
            <ArrowRight className="h-5 w-5 text-stone-400 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/client/reports" className="bg-white p-6 hover:bg-stone-50 transition-colors flex items-center justify-between group">
            <div>
              <BarChart3 className="h-6 w-6 text-[#1f4d2e] mb-2" />
              <div className="font-display text-xl font-bold">Reports</div>
              <div className="text-sm text-stone-500 mt-1">Daily / weekly / monthly hours & exports</div>
            </div>
            <ArrowRight className="h-5 w-5 text-stone-400 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link to="/client/invoices" data-testid="client-invoices-link" className="bg-white p-6 hover:bg-stone-50 transition-colors flex items-center justify-between group">
            <div>
              <Receipt className="h-6 w-6 text-[#d4a82a] mb-2" />
              <div className="font-display text-xl font-bold">Invoices</div>
              <div className="text-sm text-stone-500 mt-1">Billable hours and matrix invoice (PDF/CSV)</div>
            </div>
            <ArrowRight className="h-5 w-5 text-stone-400 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Billable (last 30d) summary */}
        {invoice && Object.keys(invoice.totals_by_currency || {}).length > 0 && (
          <div className="mb-6 p-6 rounded-md border border-[#d4a82a]/40 bg-[#d4a82a]/10 flex items-center justify-between flex-wrap gap-6" data-testid="client-billable-summary">
            <div>
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#7a5e0f] font-bold">Total Billable</div>
              <div className="text-xs text-stone-500 mt-0.5 font-mono-num">Last 30 days · {invoice.start_date} → {invoice.end_date}</div>
            </div>
            <div className="flex gap-10 flex-wrap">
              {Object.entries(invoice.totals_by_currency).map(([cur, amt]) => (
                <div key={cur}>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-stone-500">{cur}</div>
                  <div className="font-mono-num text-3xl font-bold text-[#1f4d2e]">{fmtMoney(amt, cur)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="border border-stone-200">
          <div className="px-5 py-3 border-b border-stone-200 bg-stone-50 text-xs tracking-[0.25em] uppercase font-bold">
            Assigned Employees
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-stone-200 text-[10px] tracking-[0.2em] uppercase text-stone-500">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Name</th>
                <th className="text-left px-5 py-3 font-bold">Email</th>
                <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogIn className="h-3 w-3" />Time In</span></th>
                <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogOut className="h-3 w-3" />Time Out</span></th>
                <th className="text-center px-5 py-3 font-bold">
                  <span className="inline-flex items-center gap-1.5"><Phone className="h-3 w-3" /> Call Mode</span>
                </th>
                <th className="text-right px-5 py-3 font-bold">Productive (7d)</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => {
                const u = (report?.by_user || []).find((x) => x.user_id === e.id);
                return (
                  <tr key={e.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-5 py-3 font-bold">{e.name}</td>
                    <td className="px-5 py-3 font-mono-num text-xs">{e.email}</td>
                    <td className="px-5 py-3 font-mono-num text-[#1f4d2e]">{fmtClockTime(u?.today_first_start_at)}</td>
                    <td className="px-5 py-3 font-mono-num text-[#a8421f]">
                      {u?.today_is_running ? <span className="text-stone-400">running…</span> : fmtClockTime(u?.today_last_stop_at)}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <div className="inline-flex items-center gap-2">
                        <Switch
                          data-testid={`call-mode-toggle-${e.id}`}
                          checked={!!e.call_mode_enabled}
                          onCheckedChange={(v) => toggleCallMode(e.id, v)}
                        />
                        <span className={`text-[10px] tracking-[0.18em] uppercase font-bold ${e.call_mode_enabled ? "text-[#1f4d2e]" : "text-stone-400"}`}>
                          {e.call_mode_enabled ? "Allowed" : "Off"}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono-num font-bold text-[#1f4d2e]">{fmtSeconds(u?.productive || 0)}</td>
                  </tr>
                );
              })}
              {employees.length === 0 && (
                <tr><td colSpan="6" className="text-center py-12 text-sm text-stone-500">No employees assigned yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
